import {
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
} from "@mui/material";
import { SelectProps } from "@mui/material/Select";
import { CSSProperties } from "@mui/styles";
import { getKey } from "core/model/utils/strings";
import { LockSecurePin } from "ds_legacy/components/Icons/SecurePin";
import {
  KEYBOARD_FOCUS_OUTLINE,
  TEXT_DARK_PRIMARY,
} from "ds_legacy/materials/colors";
import { border, dp } from "ds_legacy/materials/metrics";
import { FONT_SIZE_14, FONT_SIZE_16 } from "ds_legacy/materials/typography";
import { useEffect, useMemo, useState } from "react";
import {
  FieldValidation,
  FormElementRenderProps,
  isValid,
} from "react-forms-state";
import { useTranslations } from "translations";
import { getHelperText } from "../Validation";

export type SelectOption = {
  id: number | string;
  label: number | string;
  value: number | string;
};

export type SelectInputProps<
  Option extends SelectOption = SelectOption,
  Multiple extends boolean | undefined = undefined,
  Nullable extends boolean | undefined = boolean | undefined,
> = Pick<
  SelectProps,
  "disabled" | "id" | "label" | "placeholder" | "required" | "size" | "variant"
> & {
  ariaLabelledBy?: string;
  elementName?: string;
  formHelperTextSx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
  isFullWidth?: boolean;
  MenuProps?: SelectProps["MenuProps"];
  multiple?: Multiple;
  nullable?: Multiple extends true ? never : Nullable;
  onChange: (
    value: Multiple extends true
      ? Option["value"][]
      : Nullable extends true
      ? Option["value"] | null
      : Option["value"],
    statePath?: string,
  ) => void;
  options: Option[];
  secure?: string;
  style?: CSSProperties;
  validation?: FieldValidation;
  value: Multiple extends true ? Option["value"][] : Option["value"] | null;
};

export type ConnectedSelectInputProps<
  Option extends SelectOption = SelectOption,
  Multiple extends boolean | undefined = undefined,
  Nullable extends boolean | undefined = boolean | undefined,
> = Omit<
  SelectInputProps<Option, Multiple, Nullable>,
  "onChange" | "validation" | "value"
> & {
  sideMutation?: (newValue: any, mutateElement: AnyFunction) => void;
  withFlatModel?: boolean;
};

export function optionToSelectOption<
  Option extends SelectOption = SelectOption,
>(option: unknown): Option {
  return { id: option, label: option, value: option } as unknown as Option;
}

export function optionsToSelectOptions<
  Option extends SelectOption = SelectOption,
>(options: unknown[]): Option[] {
  return options.map(optionToSelectOption) as unknown as Option[];
}

const labelStyle: CSSProperties = {
  color: TEXT_DARK_PRIMARY,
  fontSize: FONT_SIZE_14,
};

const menuItemStyle = (theme: Theme): CSSProperties => ({
  ...labelStyle,
  ":hover": {
    backgroundColor: theme.palette.common.white,
    outline: `${dp(1)} solid ${theme.palette.primary.dark}`,
  },
});

export function SelectInput<
  Option extends SelectOption = SelectOption,
  Multiple extends boolean | undefined = undefined,
  Nullable extends boolean | undefined = undefined,
>({
  ariaLabelledBy,
  disabled,
  elementName,
  formHelperTextSx,
  id,
  inputSx,
  isFullWidth,
  label,
  MenuProps,
  multiple,
  nullable,
  onChange,
  options,
  placeholder,
  required,
  secure,
  size,
  style,
  validation,
  value,
  variant = "standard",
}: SelectInputProps<Option, Multiple, Nullable>) {
  const errorTextId = `${elementName}_error_text_id`;
  const translations = useTranslations();
  const [localValue, setValue] = useState<Option["value"] | Option["value"][]>(
    multiple ? [] : "",
  );
  const defaultId = id || elementName;
  const labelId = ariaLabelledBy ?? (label ? `${defaultId}-label` : undefined);
  const inputProps = label ? {} : { id: labelId };

  const handleChange = (event: SelectChangeEvent<typeof localValue>) => {
    const { value } = event.target;

    setValue(value);

    onChange(
      (multiple
        ? value
        : `${value}`?.length
        ? value
        : null) as Multiple extends true
        ? string[]
        : Nullable extends true
        ? string | null
        : string,
    );
  };

  useEffect(() => {
    setValue(
      (value != null ? value : multiple ? [] : "") as
        | Option["value"]
        | Option["value"][],
    );
  }, [value]);

  const endAdornmentElement = useMemo(() => {
    if (!secure) return null;

    const adornmentIconStyle = { fontSize: FONT_SIZE_16 };
    return (
      <InputAdornment
        position="end"
        sx={{ height: "100%", paddingBottom: dp(5) }}
      >
        <LockSecurePin style={adornmentIconStyle} tooltip={secure} />
      </InputAdornment>
    );
  }, [secure]);

  const hasError = !isValid(validation);

  return (
    <FormControl
      disabled={disabled}
      error={hasError}
      fullWidth
      required={required}
      size={size}
      sx={(theme) => ({
        ...labelStyle,
        ...style,
        "& .MuiFormLabel-root": labelStyle,
        "& .MuiInputBase-root.Mui-focused": {
          outline: border({ color: theme.palette.primary.main, width: 2 }),
          outlineOffset: dp(-2),
        },
        "& .MuiSelect-select": {
          ...labelStyle,
        },
      })}
      variant={variant}
    >
      {label && (
        <InputLabel data-testid={labelId} id={labelId}>
          {label}
        </InputLabel>
      )}
      <Select
        aria-describedby={hasError ? errorTextId : undefined}
        aria-labelledby={labelId}
        data-testid={defaultId}
        displayEmpty={nullable}
        endAdornment={endAdornmentElement}
        fullWidth={isFullWidth}
        id={defaultId}
        inputProps={inputProps}
        labelId={labelId}
        MenuProps={MenuProps}
        multiple={multiple}
        name={elementName}
        renderValue={(selected) => {
          if (multiple) {
            const selectedOptions = options.filter((option) =>
              (selected as string[]).includes(option.value as string),
            );
            return (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selectedOptions?.map(({ label, value }) => (
                  <Chip key={value} label={label} />
                ))}
              </div>
            );
          }
          const selectedOption = options.find(
            (option) => option.value === selected,
          );

          if (!selectedOption) return placeholder ?? "";

          return selectedOption.label;
        }}
        sx={{
          ...inputSx,
          "&.Mui-focused": {
            outline: `${dp(2)} solid ${KEYBOARD_FOCUS_OUTLINE}`,
            outlineOffset: dp(-2),
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          ...(secure && {
            "& .MuiSelect-icon": {
              right: dp(18),
            },
          }),
        }}
        value={localValue}
        onChange={handleChange}
      >
        {nullable && !multiple && (
          <MenuItem
            key={getKey("none")}
            data-testid="option-none"
            divider
            sx={menuItemStyle}
            value=""
          >
            <em>{translations.general.noPreference}</em>
          </MenuItem>
        )}
        {options.map(({ label, value }) => {
          return (
            <MenuItem
              key={value}
              data-testid={`option-${value}`}
              divider
              sx={menuItemStyle}
              value={value}
            >
              {multiple ? (
                <>
                  <Checkbox
                    checked={
                      (localValue as Option["value"][]).indexOf(value) > -1
                    }
                    size="small"
                    sx={labelStyle}
                  />
                  <span style={labelStyle as any}>{label}</span>
                </>
              ) : (
                label
              )}
            </MenuItem>
          );
        })}
      </Select>
      <FormHelperText
        error={!isValid(validation)}
        id={errorTextId}
        sx={formHelperTextSx}
      >
        {getHelperText({
          hasCustomValidation: true,
          translations,
          validation,
        })}
      </FormHelperText>
    </FormControl>
  );
}

export default function ConnectedSelectInput<
  Option extends SelectOption = SelectOption,
  Multiple extends boolean | undefined = undefined,
  Nullable extends boolean | undefined = undefined,
>({
  disabled,
  elementName,
  formHelperTextSx,
  id,
  inputSx,
  label,
  MenuProps,
  multiple,
  nullable,
  options,
  placeholder,
  required,
  secure,
  sideMutation,
  size,
  variant = "standard",
  withFlatModel,
  ...props
}: Omit<
  SelectInputProps<Option, Multiple, Nullable>,
  "onChange" | "validation" | "value"
> & {
  sideMutation?: (newValue: any, mutateElement: AnyFunction) => void;
  withFlatModel?: boolean;
}) {
  return (
    <FormElementRenderProps
      elementName={elementName}
      sideMutation={sideMutation}
      withFlatModel={withFlatModel}
      {...props}
    >
      {({ onChange, validation, value }) => (
        <SelectInput<Option, Multiple, Nullable>
          disabled={disabled}
          elementName={elementName}
          formHelperTextSx={formHelperTextSx}
          id={id}
          inputSx={inputSx}
          label={label}
          MenuProps={MenuProps}
          multiple={multiple}
          nullable={nullable}
          options={options}
          placeholder={placeholder}
          required={required}
          secure={secure}
          size={size}
          validation={validation}
          value={value}
          variant={variant}
          onChange={onChange}
        />
      )}
    </FormElementRenderProps>
  );
}
